import type { ErrorResponse } from '@pin/schemas/shared';
import { retry, throwError, timer } from 'rxjs';

export class HttpError extends Error {
  response: Response;
  body: ErrorResponse | null = null;
  constructor(response: Response) {
    super(`HTTP error ${response.status}: ${response.statusText}`);
    this.name = 'HttpError';
    this.response = response;
  }

  async parseBody(): Promise<ErrorResponse | null> {
    if (this.body) {
      return this.body;
    }
    try {
      this.body = await this.response.json();
    } catch {
      this.body = { error: 'unknown', message: this.message };
    }
    return this.body;
  }
}

/**
 * Uniformly jitter a delay to [0, delayMs] so concurrent retries spread out.
 */
const fullJitter = (delayMs: number, random: () => number = Math.random) =>
  random() * delayMs;

/**
 * Jitter a server-provided floor additively to [floorMs, 1.25 * floorMs].
 */
const additiveJitter = (floorMs: number, random: () => number = Math.random) =>
  floorMs * (1 + 0.25 * random());

/**
 * Retry operator that retries a fetch operation with exponential backoff.
 *
 * `retryWithBackoff(3, status => status === 429 || status >= 500)` retries up to 3 times with exponential backoff and jitter,
 * honoring Retry-After header and skipping non-retriable status codes.
 */
export const retryWithBackoff = <T>(
  count: number,
  retryStatusCode: (status: number) => boolean = (status) =>
    status === 429 || status >= 500,

  maxDelayMs = 60_000
) => {
  return retry<T>({
    count,
    delay: (error: unknown, retryCount: number) => {
      const exponentialBackoff = fullJitter(
        Math.min(1000 * 2 ** (retryCount - 1), maxDelayMs)
      );
      if (error instanceof HttpError) {
        if (!retryStatusCode(error.response.status)) {
          return throwError(() => error);
        }
        return timer(
          Math.max(
            additiveJitter(parseRetryAfterHeader(error.response) ?? 0),
            exponentialBackoff
          )
        );
      }
      return timer(exponentialBackoff);
    },
    resetOnSuccess: true,
  });
};

/**
 * Parses the 'Retry-After' header from an HTTP response.
 *
 * This header can be either:
 * - A number of milliseconds to wait before retrying, or
 * - An string date indicating when to retry.
 *
 * @param response - The HTTP Response object to extract the header from.
 * @returns The delay in milliseconds before retrying, or null if the header is missing or invalid.
 */
const parseRetryAfterHeader = (response: Response): number | null => {
  const retryAfter = response.headers.get('retry-after');
  if (!retryAfter) {
    return null;
  }

  const seconds = Number(retryAfter);
  if (!Number.isNaN(seconds)) {
    return seconds * 1000;
  }

  const date = Date.parse(retryAfter);
  if (Number.isNaN(date)) {
    return null;
  }

  return date - Date.now();
};
